import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Typography } from "@mui/material";
import { Feature, LineString, MultiLineString } from "geojson";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery } from "fond/api/layersSlice";
import { buildUgPathGraph } from "fond/draw/modes/base/ugPathGraph";
import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import {
  DeleteButton,
  LineStringButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  ShortestPathButton,
  SnapAddButton,
  SnapDeleteButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { MuPowerEditableLayerIds, MuPowerSolverLayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [
      <LineStringButton key="drawLine" />,
      <SnapAddButton key="snapAdd" />,
      <SnapDeleteButton key="snapDelete" />,
      <ShortestPathButton key="shortestPath" />,
    ],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const preservePropertiesFor = (layerId: string) => (feature: Feature) => ({
  layerId,
  ...(feature.properties?.sourceUgPathId != null && { sourceUgPathId: feature.properties.sourceUgPathId }),
});

const isLineFeature = (f: Feature): f is Feature<LineString | MultiLineString> =>
  f.geometry.type === "LineString" || f.geometry.type === "MultiLineString";

const MuPowerFootprintDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { clear } = useStackedNavigationContext();

  const ugPathLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerEditableLayerIds.ugPath }));
  const { data: ugPathFeatures } = useGetFeaturesQuery({ layerId: ugPathLayer?.ID ?? "" }, { skip: !ugPathLayer?.ID });

  const ugPathGraph = useMemo(() => {
    if (!ugPathFeatures) return undefined;
    return buildUgPathGraph(ugPathFeatures.filter(isLineFeature));
  }, [ugPathFeatures]);

  const options: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapVertexPriorityDistance: 0.000025,
      },
      snapSourceLayer: {
        source: "project-layers",
        sourceLayer: MuPowerEditableLayerIds.ugPath,
      },
      shortestPathGraph: ugPathGraph,
      editOptions: {
        allLayers: [
          {
            ID: MuPowerSolverLayerIds.primaryFootprint,
            GeometryType: "LineString",
            getFeatureProperties: preservePropertiesFor(MuPowerSolverLayerIds.primaryFootprint),
          },
          {
            ID: MuPowerSolverLayerIds.secondaryFootprint,
            GeometryType: "LineString",
            getFeatureProperties: preservePropertiesFor(MuPowerSolverLayerIds.secondaryFootprint),
          },
        ],
        editingLayerId: MuPowerSolverLayerIds.primaryFootprint,
        editingLayerIds: [MuPowerSolverLayerIds.primaryFootprint, MuPowerSolverLayerIds.secondaryFootprint],
      },
    }),
    [ugPathGraph]
  );

  return (
    <MultiLayerEditProvider versionId={versionId} options={options} config={toolbarConfig}>
      <MultiLayerEditPanelContent
        title="Edit footprint"
        alertTitle="Draw footprint"
        alertBody={<Typography variant="body2">Switch between Primary and Secondary using the layer selector on the map.</Typography>}
        onFinish={clear}
      />
    </MultiLayerEditProvider>
  );
};

export default MuPowerFootprintDraw;
